import { useState } from "react";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import Card from "react-bootstrap/Card";
import { FaDatabase } from "react-icons/fa";

import { Paginated } from "@/@types/generics/paginated";
import { GenericObject } from "@/@types/generics/genericals";
import { getQuarterDates } from "@/utils/quarters";
import { exportExcel } from "@/utils/exports";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { getIndicatorClassification, IndicatorEsfEnum } from "@/utils/indicatorsEsf";
import { ResumeIndicatorC2ByHealthTeamData } from "@/services/esus/indicator/esf/c2/getResumeIndicatorC2ByHealthTeam";

import { IndicatorEsfC2HealthTeamsFilters } from "./IndicatorEsfC2HealthTeamsFilters";
import { IndicatorEsfC2HealthTeamsClassificationTable } from "./IndicatorEsfC2HealthTeamsTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { LoadingScreen } from "@/components/LoadingScreen";

type IndicatorEsfC2HealthTeamsProps = {
	selectedQuarter: string;
	search: string;
	setSearch: (search: string) => void;
	isLoading: boolean;
	paginatedHealthTeamsClassifications: Paginated<ResumeIndicatorC2ByHealthTeamData>;
	pageNumber: number;
	setPageNumber: (pageNumber: number) => void;
	handleGetAllResumeIndicatorC2ByHealthTeam: () => Promise<ResumeIndicatorC2ByHealthTeamData[]>;
};

export function IndicatorEsfC2HealthTeams({
	selectedQuarter,
	search,
	setSearch,
	isLoading,
	paginatedHealthTeamsClassifications,
	pageNumber,
	setPageNumber,
	handleGetAllResumeIndicatorC2ByHealthTeam,
}: Readonly<IndicatorEsfC2HealthTeamsProps>) {
	const [isLoadingExportData, setIsLoadingExportData] = useState(false);
	async function handleExportTable() {
		setIsLoadingExportData(true);
		const healthTeamsClassifications = await handleGetAllResumeIndicatorC2ByHealthTeam();
		setIsLoadingExportData(false);
		const quarterDates = getQuarterDates(selectedQuarter);

		if (Object.keys(healthTeamsClassifications).length > 0) {
			const excelData: GenericObject[] = transformIndicatorC2AttendancesToGenericObjectArray(
				healthTeamsClassifications
			);
			exportExcel(
				excelData,
				`Atendimentos por equipe - ${dayjs(quarterDates.startDate).format(
					"DD-MM-YYYY"
				)} à ${dayjs(quarterDates.endDate).format("DD/MM/YYYY")}`
			);
		}
	}

	function transformIndicatorC2AttendancesToGenericObjectArray(
		healthTeamClassifications: ResumeIndicatorC2ByHealthTeamData[]
	) {
		return healthTeamClassifications.map(
			(healthTeamClassification) =>
				({
					"Equipe do atendimento": healthTeamClassification.healthTeam?.name ?? "",
					"Total de crianças":
						healthTeamClassification.totalCitizensWithTwoYearsInPeriod,
					Pontuação: healthTeamClassification.totalCitizensWithTwoYearsInPeriodScore,
					"% Classificação": `${calculatePercentage(
						healthTeamClassification.totalCitizensWithTwoYearsInPeriodScore,
						healthTeamClassification.maxCitizensWithTwoYearsInPeriodScore
					)}%`,
					Classificação: getIndicatorClassification(
						IndicatorEsfEnum.C2,
						calculatePercentage(
							healthTeamClassification.totalCitizensWithTwoYearsInPeriodScore,
							healthTeamClassification.maxCitizensWithTwoYearsInPeriodScore
						)
					),
				} as GenericObject)
		);
	}

	return (
		<Row className="bg-white m-3 p-2">
			<Col>
				<LoadingScreen loading={isLoadingExportData} />
				<Card>
					<Card.Header>
						<IndicatorEsfC2HealthTeamsFilters
							search={search}
							setSearch={setSearch}
							handleExportTable={handleExportTable}
						/>
					</Card.Header>
					<Card.Body>
						<IndicatorEsfC2HealthTeamsClassificationTable
							healthTeamsClassifications={paginatedHealthTeamsClassifications.data}
							isLoading={isLoading}
						/>
					</Card.Body>
					<Card.Footer>
						{paginatedHealthTeamsClassifications?.totalRecords ? (
							<PaginationLinks
								itemsPerPage={paginatedHealthTeamsClassifications.pageSize}
								totalPages={paginatedHealthTeamsClassifications.totalRecords}
								changeSelectedPage={(pageNumber) => {
									setPageNumber(pageNumber);
								}}
								pageNumber={pageNumber || 0}
							/>
						) : (
							!isLoading && (
								<NoRecordsFeedback
									message={"Nenhum resultado encontrado."}
									icon={<FaDatabase />}
								/>
							)
						)}
					</Card.Footer>
				</Card>
			</Col>
		</Row>
	);
}
